import { css } from '@emotion/react';
import {
  black,
  gray1,
  gray2,
  gray4,
  gray6,
  info,
  kelp,
  primary,
  success,
  white,
} from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  extraLargeSpacing,
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
  threeQuartersSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import {
  screenSmMax,
  screenXsMax,
} from 'styles/global_defaults/media-queries';

const successGradient = `linear-gradient(145deg, ${success} 0%, ${kelp} 100%)`;
const compactLayoutMaxWidth = screenXsMax + 1;
const oneColumnStatsMaxWidth = 480;

const fullPageStyles = css`
  position: relative;
  min-height: 100vh;
  background: ${info};
  overflow: hidden;
  padding: ${tripleSpacing}px ${standardSpacing}px ${extraLargeSpacing}px;

  &:before {
    content: '';
    position: absolute;
    top: -220px;
    right: -120px;
    width: 820px;
    height: 520px;
    border-radius: 55%;
    background: rgba(147, 218, 225, 0.5);
    z-index: 0;
  }

  &:after {
    content: '';
    position: absolute;
    top: 260px;
    left: -300px;
    width: 960px;
    height: 520px;
    border-radius: 60%;
    background: rgba(255, 255, 255, 0.2);
    z-index: 0;
  }

  .takeaways-content {
    position: relative;
    z-index: 1;
    max-width: 800px;
    margin: 0 auto;
    text-align: center;
  }

  .mobile-burger-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 56px;
    display: none;
    align-items: center;
    padding: 0 ${halfSpacing}px;
    background: transparent;
    transition: background-color 120ms ease, box-shadow 120ms ease;
    z-index: 3;

    &.scrolled {
      background: ${white};
      box-shadow: 0 1px 0 0 ${gray4};
    }
  }

  .mobile-burger-button {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 6px;
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${black};
    padding: 0;
  }

  .takeaways-spark {
    display: inline-flex;
    margin-bottom: ${quarterSpacing}px;
  }

  .eyebrow {
    color: ${black};
    margin-bottom: ${halfSpacing}px;
  }

  .main-title {
    color: ${black};
    margin-bottom: ${tripleSpacing}px;
  }

  .divider {
    width: 100px;
    height: 1px;
    background: ${gray4};
    margin: 0 auto ${standardSpacing}px;
  }

  .section {
    margin-bottom: ${doubleSpacing}px;

    .section-kicker {
      color: ${black};
      margin-bottom: ${halfSpacing}px;
    }

    .section-title {
      color: ${gray1};
      margin-bottom: ${halfSpacing}px;
    }
  }

  .section-copy {
    max-width: 640px;
    margin: 0 auto;
  }

  .section-headline,
  .section-summary,
  .section-intro,
  .section-empty {
    color: ${gray1};
  }

  .section-headline {
    margin-bottom: ${standardSpacing}px;
  }

  .section-list {
    color: ${gray1};
    margin: ${standardSpacing}px auto 0;
    padding-left: ${standardSpacing}px;
    text-align: left;

    li + li {
      margin-top: ${standardSpacing}px;
    }
  }

  .resource-list {
    list-style: none;
    padding-left: 0;

    li {
      display: flex;
      flex-direction: column;
      gap: ${quarterSpacing}px;
    }
  }

  .resource-title {
    color: ${black};
  }

  .generating-block {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${quarterSpacing}px;
    min-height: 70px;
  }

  .generating-label {
    color: ${gray2};
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: ${halfSpacing}px;
    margin-top: ${standardSpacing}px;
  }

  .stats-card {
    min-height: 105px;
    border-radius: ${halfSpacing}px;
    border: 1px solid rgba(255, 255, 255, 0.7);
    box-shadow: 0 10px 30px 0 ${gray6};
    background: rgba(255, 255, 255, 0.55);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${halfSpacing}px;
    gap: ${quarterSpacing}px;
  }

  .stat-label {
    color: ${black};
  }

  .stat-value {
    color: ${success};
  }

  .mini-spinner {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid rgba(87, 99, 115, 0.2);
    border-top-color: rgba(87, 99, 115, 0.8);
    animation: spin 0.9s linear infinite;
  }

  .completion-badge {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: ${successGradient};
    margin: 0 auto ${standardSpacing}px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 2px solid rgba(255, 255, 255, 0.8);

    .congrats-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .completion-copy {
    max-width: 640px;
    margin: 0 auto;

    .section-title {
      margin-bottom: ${standardSpacing}px;
    }

    .completion-body {
      color: ${gray1};
      margin-bottom: ${largeSpacing}px;
    }
  }

  .back-button {
    min-height: 35px;
    border: none;
    border-radius: ${quarterSpacing}px;
    background: ${primary};
    color: ${white};
    padding: ${quarterSpacing}px ${threeQuartersSpacing}px;
  }

  .note {
    margin-top: ${largeSpacing}px;
    color: ${gray2};
  }

  @media (max-width: ${screenSmMax}px) {
    .stats-row {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: ${compactLayoutMaxWidth}px) {
    padding: ${doubleSpacing}px ${halfSpacing}px ${doubleSpacing}px;

    .main-title {
      margin-bottom: ${doubleSpacing}px;
    }

    .stats-row {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: ${screenSmMax}px) {
    .mobile-burger-bar {
      display: flex;
    }
  }

  @media (max-width: ${oneColumnStatsMaxWidth}px) {
    .stats-row {
      grid-template-columns: 1fr;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default fullPageStyles;
